import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./ReportsSectionCompact.css";
import { useNavigate } from "react-router-dom";
import ReportHelpCard from "../components/reports/ReportHelpCard";
import ReportPageHeader from "../components/reports/ReportPageHeader";
import {
  getSuperAdminExtendedReportFilters,
  postSuperAdminExtendedReport,
} from "../api/superadminReports";
import { useAuth } from "../context/AuthContext";
import useHelpGuideNavigation from "../hooks/useHelpGuideNavigation";
import {
  BTN_OUTLINE,
  BTN_PRIMARY,
  FIELD_CLASS,
  LABEL_TEXT,
  MODAL_TEXT,
} from "../ui/modalStyles";

const REPORT_META = {
  "marketing-campaigns": {
    title: "Marketing Campaign Performance",
    subtitle:
      "Track campaign performance across all salons, one business, or one salon.",
  },
  "email-subscription": {
    title: "Email Subscription Stats",
    subtitle:
      "Review opt-in, opt-out, and email preference totals across the selected scope.",
  },
  "walk-in-queue": {
    title: "Walk-In Queue",
    subtitle:
      "Review walk-in queue volume, revenue, overrides, and auto-skips across the selected scope.",
  },
  "profitability-estimate": {
    title: "Profitability Estimate",
    subtitle:
      "Compare revenue, saved expense estimates, and estimated profit across the selected scope.",
  },
  "ticket-summary": {
    title: "Ticket Summary",
    subtitle:
      "Summarize tickets, sales, tax, and gross revenue across the selected scope.",
  },
  "daily-closing": {
    title: "Daily Closing Cash Report",
    subtitle:
      "Review daily closing totals across all salons, one business, or one salon.",
  },
  "cash-balance": {
    title: "Cash Balance Report",
    subtitle:
      "Track sales and cash net totals across all salons, one business, or one salon.",
  },
  "sales-tickets": {
    title: "Sales & Tickets",
    subtitle:
      "Review ticket volume, booking sources, ticket types, and sales across the selected scope.",
  },
  "training-progress": {
    title: "Training Progress",
    subtitle:
      "Track modules, assignments, started items, and completions across the selected scope.",
  },
  "gift-cards-cash-drawer": {
    title: "Cash Drawer Analysis",
    subtitle:
      "Review cash net, tips withdrawn, and bonuses across the selected scope.",
  },
};

const SCOPE_OPTIONS = [
  { value: "all", label: "All Salons" },
  { value: "business", label: "Business" },
  { value: "salon", label: "Salon" },
];

const formatDateISO = (value) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return "";
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const addDays = (base, days) => {
  const next = new Date(base.getTime());
  next.setDate(next.getDate() + days);
  return next;
};

const formatValue = (value, type) => {
  if (type === "money") {
    const number = Number(value || 0);
    return `$${number.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }
  if (type === "percent") {
    const number = Number(value || 0);
    return `${number.toFixed(2)}%`;
  }
  if (type === "datetime" && value) {
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) return date.toLocaleString();
  }
  if (type === "date" && value) return String(value);
  if (value === null || value === undefined || value === "") return "--";
  return String(value);
};

export default function SuperAdminExtendedReport({ reportKey }) {
  const navigate = useNavigate();
  const { is_super_admin } = useAuth();
  const { jumpToSectionWithHighlight, getSectionClassName } =
    useHelpGuideNavigation();
  const meta = REPORT_META[reportKey] || {
    title: "Super Admin Report",
    subtitle: "Review this report across the selected scope.",
  };
  const today = useMemo(() => formatDateISO(new Date()), []);
  const defaultStart = useMemo(() => formatDateISO(addDays(new Date(), -29)), []);

  const [scope, setScope] = useState("all");
  const [businessId, setBusinessId] = useState("");
  const [salonId, setSalonId] = useState("");
  const [includeColumns, setIncludeColumns] = useState(true);
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(today);
  const [businesses, setBusinesses] = useState([]);
  const [salons, setSalons] = useState([]);
  const [filtersError, setFiltersError] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [report, setReport] = useState(null);
  const autoRunRef = useRef(false);

  const filteredSalons = useMemo(() => {
    if (!businessId) return salons;
    return salons.filter((entry) => String(entry.business_id) === String(businessId));
  }, [businessId, salons]);

  const canRun =
    is_super_admin &&
    startDate &&
    endDate &&
    endDate >= startDate &&
    (scope !== "business" || businessId) &&
    (scope !== "salon" || salonId);

  const loadFilters = useCallback(async () => {
    if (!is_super_admin) return;
    setFiltersError("");
    try {
      const data = await getSuperAdminExtendedReportFilters({
        scope,
        business_id: businessId || undefined,
        salon_id: salonId || undefined,
      });
      setBusinesses(Array.isArray(data?.businesses) ? data.businesses : []);
      setSalons(Array.isArray(data?.salons) ? data.salons : []);
    } catch (err) {
      setBusinesses([]);
      setSalons([]);
      setFiltersError(err?.message || "Unable to load report filters.");
    }
  }, [businessId, is_super_admin, salonId, scope]);

  useEffect(() => {
    loadFilters();
  }, [loadFilters]);

  useEffect(() => {
    autoRunRef.current = false;
  }, [reportKey, scope, businessId, salonId]);

  const runReport = useCallback(async () => {
    if (!canRun) return;
    setLoading(true);
    setError("");
    try {
      const data = await postSuperAdminExtendedReport(reportKey, {
        scope,
        business_id: businessId ? Number(businessId) : null,
        salon_id: salonId ? Number(salonId) : null,
        start_date: startDate,
        end_date: endDate,
        include_business_salon_cols: includeColumns,
      });
      setReport(data);
    } catch (err) {
      setReport(null);
      setError(err?.message || "Unable to run report.");
    } finally {
      setLoading(false);
    }
  }, [
    businessId,
    canRun,
    endDate,
    includeColumns,
    reportKey,
    salonId,
    scope,
    startDate,
  ]);

  const exportCsv = () => {
    const columns = report?.columns || [];
    const rows = report?.rows || [];
    if (!columns.length || !rows.length) return;
    const csv = [
      columns.map((column) => `"${String(column.label || "").replace(/"/g, '""')}"`),
      ...rows.map((row) =>
        columns.map((column) => {
          const value = formatValue(row[column.key], column.type);
          return `"${String(value || "").replace(/"/g, '""')}"`;
        }),
      ),
    ]
      .map((line) => line.join(","))
      .join("\n");
    const blob = new Blob([new Uint8Array([0xef, 0xbb, 0xbf]), csv], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `superadmin-${reportKey}-${startDate}-to-${endDate}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    if (!canRun || autoRunRef.current) return;
    autoRunRef.current = true;
    runReport();
  }, [canRun, runReport]);

  if (!is_super_admin) {
    return (
      <div className="px-4 py-6">
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          Super Admin access required.
        </div>
      </div>
    );
  }

  return (
    <div className="reportSectionShell min-h-screen bg-slate-100">
      <div className="reportSectionWide report-section-spacious report-section-compact">
        <div className="rounded-3xl bg-white shadow-2xl overflow-hidden">
          <ReportPageHeader title={meta.title} closeTo="/superadmin/reports" />
          <div className={`px-6 py-6 space-y-6 ${MODAL_TEXT}`}>
            <div id="sa-extended-overview">
              <ReportHelpCard
                title={`Help Guide: ${meta.title}`}
                subtitle={meta.subtitle}
                sections={[
                  {
                    id: "filters",
                    title: "Filters",
                    description:
                      "Use scope, business, salon, date range, and shared location filters to narrow the report.",
                    anchorId: "sa-extended-filters",
                    bullets: [
                      "All Salons includes every salon matching the shared location filters.",
                      "Business limits the report to one business.",
                      "Salon limits the report to one salon.",
                    ],
                    tips: [],
                  },
                  {
                    id: "results",
                    title: "Results",
                    description:
                      "Review totals and table rows for the selected scope.",
                    anchorId: "sa-extended-results",
                    bullets: ["Broad scopes include Business and Salon columns when enabled."],
                    tips: [],
                  },
                ]}
                troubleshooting={[
                  {
                    title: "No rows showing",
                    body: "Check the date range, shared location filters, and selected business or salon.",
                  },
                ]}
                onJumpToSection={jumpToSectionWithHighlight}
              />
            </div>

            {(error || filtersError) && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error || filtersError}
              </div>
            )}

            <div
              id="sa-extended-filters"
              className={`rounded-2xl border border-slate-200 bg-white p-4 space-y-4 ${getSectionClassName(
                "sa-extended-filters",
              )}`}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                <div>
                  <label className={LABEL_TEXT}>Scope</label>
                  <select
                    value={scope}
                    onChange={(event) => {
                      setScope(event.target.value);
                      setBusinessId("");
                      setSalonId("");
                    }}
                    className={`${FIELD_CLASS} w-full`}
                  >
                    {SCOPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={LABEL_TEXT}>Business</label>
                  <select
                    value={businessId}
                    onChange={(event) => {
                      setBusinessId(event.target.value);
                      setSalonId("");
                    }}
                    className={`${FIELD_CLASS} w-full`}
                    disabled={scope === "all"}
                  >
                    <option value="">
                      {scope === "all" ? "All Businesses" : "Select business"}
                    </option>
                    {businesses.map((business) => (
                      <option key={business.id} value={business.id}>
                        {business.name || `Business ${business.id}`}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={LABEL_TEXT}>Salon</label>
                  <select
                    value={salonId}
                    onChange={(event) => setSalonId(event.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                    disabled={scope !== "salon"}
                  >
                    <option value="">
                      {scope === "salon" ? "Select salon" : "Salon filter disabled"}
                    </option>
                    {filteredSalons.map((salon) => (
                      <option key={salon.id} value={salon.id}>
                        {salon.name || `Salon ${salon.id}`}
                      </option>
                    ))}
                  </select>
                </div>
                <label className="flex items-center gap-2 rounded-2xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700">
                  <input
                    type="checkbox"
                    checked={includeColumns}
                    onChange={(event) => setIncludeColumns(event.target.checked)}
                    disabled={scope === "salon"}
                  />
                  Show Business/Salon columns
                </label>
                <div>
                  <label className={LABEL_TEXT}>Start Date</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(event) => setStartDate(event.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                  />
                </div>
                <div>
                  <label className={LABEL_TEXT}>End Date</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(event) => setEndDate(event.target.value)}
                    className={`${FIELD_CLASS} w-full`}
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={runReport}
                  className={BTN_PRIMARY}
                  disabled={!canRun || loading}
                >
                  {loading ? "Running..." : "Run Report"}
                </button>
              </div>
            </div>

            {report?.notes?.length ? (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                {report.notes.join(" ")}
              </div>
            ) : null}

            <div
              id="sa-extended-results"
              className={`space-y-4 ${getSectionClassName("sa-extended-results")}`}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                {(report?.totals || []).map((item) => (
                  <div
                    key={item.label}
                    className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
                  >
                    <div className="text-xs font-semibold uppercase text-slate-500">
                      {item.label}
                    </div>
                    <div className="mt-2 text-xl font-semibold text-slate-900">
                      {formatValue(item.value, item.type)}
                    </div>
                  </div>
                ))}
              </div>

              <div className="rounded-2xl border border-slate-200 overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr>
                      {(report?.columns || []).map((column) => (
                        <th
                          key={column.key}
                          className="px-4 py-3 text-left font-semibold"
                        >
                          {column.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {loading ? (
                      <tr>
                        <td
                          colSpan={Math.max((report?.columns || []).length, 1)}
                          className="px-4 py-6 text-center text-slate-500"
                        >
                          Running report...
                        </td>
                      </tr>
                    ) : (report?.rows || []).length ? (
                      report.rows.map((row, index) => (
                        <tr key={`${row.salon_name || "row"}-${index}`}>
                          {(report?.columns || []).map((column) => (
                            <td key={column.key} className="px-4 py-3 text-slate-700">
                              {formatValue(row[column.key], column.type)}
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={Math.max((report?.columns || []).length, 1)}
                          className="px-4 py-6 text-center text-slate-500"
                        >
                          No rows found for this scope and date range.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={exportCsv}
                  className={BTN_OUTLINE}
                  disabled={!report?.rows?.length}
                >
                  Export CSV
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/superadmin/reports")}
                  className={BTN_OUTLINE}
                >
                  Back to Reports
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
